import { useCallback, useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useDoctorContext } from "@contexts";
import { useMigrationState } from "./useMigrationState";

/** `error.name` of the back's answer to a doctor past their deadline. */
const VALIDATION_DEADLINE_PASSED_ERROR = "ValidationDeadlinePassedError";

export interface MissingDoctorRequirements {
  fea: boolean;
  address: boolean;
  /** Past the deadline to validate their identity for the new signing provider. */
  bpoValidation: boolean;
}

interface UseDoctorDocumentReadinessOptions {
  requestId?: string;
  redirectTo?: string;
  /**
   * For the screens that create a document: check the block as soon as the
   * screen opens, so a blocked doctor is stopped before filling the form
   * whichever way they reached it, and closing the modal takes them back to
   * their requests.
   */
  stopBlockedOnOpen?: boolean;
}

export interface DoctorDocumentReadinessModalProps {
  missing: MissingDoctorRequirements | null;
  onClose: () => void;
  requestId?: string;
  redirectTo: string;
}

export const useDoctorDocumentReadiness = (
  options: UseDoctorDocumentReadinessOptions = {},
) => {
  const doctor = useDoctorContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { refetch: refetchMigrationState } = useMigrationState();
  const [missing, setMissing] = useState<MissingDoctorRequirements | null>(null);

  /** FEA enrolment and the address, which every document needs. */
  const missingBasics = useCallback((): MissingDoctorRequirements | null => {
    const needsFea = !doctor.isFeaAuthenticated;
    const needsAddress = !doctor.region || !doctor.commune || !doctor.street;
    return needsFea || needsAddress
      ? { fea: needsFea, address: needsAddress, bpoValidation: false }
      : null;
  }, [doctor.isFeaAuthenticated, doctor.region, doctor.commune, doctor.street]);

  const ensureReady = useCallback(
    (onReady: () => void) => {
      const basics = missingBasics();
      if (basics) {
        setMissing(basics);
        return false;
      }
      onReady();
      return true;
    },
    [missingBasics],
  );

  /**
   * Whether the doctor may not create documents until they validate their
   * identity, whatever signature the document takes. Read fresh every time: a cached answer from before the deadline must not
   * let a document through after it. A failed read does not block: the signing
   * endpoint still rejects a blocked doctor, and a failed read must not stop
   * anyone. Waiting on an in-flight read instead of cancelling it keeps two
   * quick clicks from turning into a failed read.
   */
  const checkSigningBlock = useCallback(async () => {
    const result = await refetchMigrationState({ cancelRefetch: false });
    const blocked = !result.isError && result.data?.signingBlocked === true;
    if (blocked) {
      setMissing({ fea: false, address: false, bpoValidation: true });
    }
    return blocked;
  }, [refetchMigrationState]);

  /**
   * `ensureReady` for creating a document: the basics, then the block. Plain
   * `ensureReady` is for what creates no document, like sharing the patient link.
   */
  const ensureReadyToIssue = useCallback(
    async (onReady: () => void) => {
      const basics = missingBasics();
      if (basics) {
        setMissing(basics);
        return false;
      }
      if (await checkSigningBlock()) return false;
      onReady();
      return true;
    },
    [missingBasics, checkSigningBlock],
  );

  /**
   * For a document the back rejected because the deadline passed while the
   * doctor was filling it: opens the same modal. Says whether it did, so the
   * caller shows its own error otherwise.
   */
  const stopIfDeadlinePassed = useCallback((error: unknown) => {
    const blocked =
      isAxiosError(error) &&
      error.response?.data?.error?.name === VALIDATION_DEADLINE_PASSED_ERROR;
    if (blocked) {
      setMissing({ fea: false, address: false, bpoValidation: true });
    }
    return blocked;
  }, []);

  const { stopBlockedOnOpen = false } = options;

  useEffect(() => {
    if (stopBlockedOnOpen) checkSigningBlock();
  }, [stopBlockedOnOpen, checkSigningBlock]);

  const onClose = useCallback(() => {
    const blocked = missing?.bpoValidation === true;
    setMissing(null);
    if (stopBlockedOnOpen && blocked) navigate("/app/solicitudes");
  }, [missing, stopBlockedOnOpen, navigate]);

  const modalProps: DoctorDocumentReadinessModalProps = useMemo(
    () => ({
      missing,
      onClose,
      requestId: options.requestId,
      redirectTo: options.redirectTo ?? location.pathname,
    }),
    [missing, onClose, options.requestId, options.redirectTo, location.pathname],
  );

  return {
    ensureReady,
    ensureReadyToIssue,
    checkSigningBlock,
    stopIfDeadlinePassed,
    modalProps,
  };
};
